import { useCallback } from 'react';
import { useTranslate, TFnType } from '@tolgee/react';
import { DescendantExecuteContext } from 'views/Aide/Scenario/PolicyForm';

export const isLongTermValidity = (
  termOfValidity: NightWatch.TermOfValidity,
): termOfValidity is NightWatch.LongTermValidity => termOfValidity.termType === 'LONG_TERM';
export const isRangeTermValidity = (
  termOfValidity: NightWatch.TermOfValidity,
): termOfValidity is NightWatch.RangeTermValidity =>
  termOfValidity.termType === 'RANGE' && termOfValidity.termRange.repeatType === 'NO_REPEAT';
export const isDayRepeatTermValidity = (
  termOfValidity: NightWatch.TermOfValidity,
): termOfValidity is NightWatch.DayRepeatTermValidity =>
  termOfValidity.termType === 'RANGE' && termOfValidity.termRange.repeatType === 'DAY';
export const isWeekRepeatTermValidity = (
  termOfValidity: NightWatch.TermOfValidity,
): termOfValidity is NightWatch.WeekRepeatTermValidity =>
  termOfValidity.termType === 'RANGE' && termOfValidity.termRange.repeatType === 'WEEK';
export const isYearRepeatTermValidity = (
  termOfValidity: NightWatch.TermOfValidity,
): termOfValidity is NightWatch.YearRepeatTermValidity =>
  termOfValidity.termType === 'RANGE' && termOfValidity.termRange.repeatType === 'YEAR';

function termOfValidityRenderWithoutTry(termOfValidity: NightWatch.TermOfValidity, t: TFnType) {
  if (isLongTermValidity(termOfValidity)) {
    return t('marketing.长期');
  }

  const rangeRender = ({ startAt, endAt }: NightWatch.Range) => `[${startAt} ~ ${endAt}]`;

  if (isRangeTermValidity(termOfValidity)) {
    const {
      termRange: { dtRange },
    } = termOfValidity;

    return rangeRender(dtRange);
  }

  const withRange = (timeRepeat: string, timeRange?: NightWatch.Range) =>
    timeRange && timeRange.startAt
      ? timeRange.endAt
        ? t('aide.timeRange期间，timeRepeat', { timeRange: rangeRender(timeRange), timeRepeat })
        : t('aide.自startTime起，timeRepeat', { startTime: timeRange.startAt, timeRepeat })
      : timeRepeat;

  if (isDayRepeatTermValidity(termOfValidity)) {
    const {
      termRange: { dtRange, dayRepeat },
    } = termOfValidity;

    const timeRepeat = dayRepeat.map(rangeRender).join(' ');

    return withRange(t('aide.每天timeRepeat循环生效', { timeRepeat }), dtRange);
  }

  if (isWeekRepeatTermValidity(termOfValidity)) {
    const {
      termRange: { dtRange, weekRepeat },
    } = termOfValidity;

    const weekLabel = {
      MONDAY: t('multiTimeSelect.周一'),
      TUESDAY: t('multiTimeSelect.周二'),
      WEDNESDAY: t('multiTimeSelect.周三'),
      THURSDAY: t('multiTimeSelect.周四'),
      FRIDAY: t('multiTimeSelect.周五'),
      SATURDAY: t('multiTimeSelect.周六'),
      SUNDAY: t('multiTimeSelect.周日'),
    };

    const repeatTime = weekRepeat
      .map(({ dayOfWeeks, timeRanges }) => {
        return t('aide.每周的days timeRepeat', {
          days: dayOfWeeks.map((week) => weekLabel[week]).join(' '),
          timeRepeat: timeRanges.map(rangeRender).join(' '),
        });
      })
      .join(', ');

    return withRange(t('aide.repeatTime循环生效', { repeatTime }), dtRange);
  }

  if (isYearRepeatTermValidity(termOfValidity)) {
    const {
      termRange: { yearRepeat },
    } = termOfValidity;

    const timeRepeat = yearRepeat.map(rangeRender).join(' ');

    return t('aide.每年timeRepeat循环生效', { timeRepeat });
  }

  return '-';
}
export function termOfValidityRender(termOfValidity: NightWatch.TermOfValidity, t: TFnType) {
  try {
    return termOfValidityRenderWithoutTry(termOfValidity, t);
  } catch (err) {
    console.log(err);

    return '-';
  }
}

export function useTermOfValidityRender() {
  const { t } = useTranslate();

  return {
    t,
    termOfValidityRender: useCallback(
      (termOfValidity: NightWatch.TermOfValidity) => termOfValidityRender(termOfValidity, t),
      [t],
    ),
  };
}

export interface FormValues
  extends Util.PartialPartial<
    Omit<NightWatch.Policy, 'replyContent'>,
    'fallbackPolicyId' | 'priority'
  > {
  replyContent: DescendantExecuteContext[];
}

export type FV = FormValues;
